import TieringHistory from "./TieringHistory";
import { useState, useEffect, useMemo } from "react";
import {
  AlertTypes,
  setAlert,
  Grid,
  Typography,
  Paper,
  Button,
  Box,
  TextField,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  Chip,
  DynamicDatagrid,
  GridColDef,
  MuiIcons,
} from "@leap4/frontend";
import { useAppDispatch } from "../../../store/hooks";
import { urlService } from "../../routing/urlsService";
import ReportHeader from "../common/ReportHeader/ReportHeader";
import {
  useGetAdvancedTargetingSettings,
  useGetAdvancedTargetingLogs,
  updateAdvancedTargetingSettings,
} from "../../../api/generated/api";
import type {
  AdvancedTargetingRunLogDto,
  GetAdvancedTargetingLogsJobName,
  UpdateAdvancedTargetingSettingsDto,
} from "../../../api/generated/models";

const {
  TrackChanges: TargetingIcon,
  Save: SaveIcon,
  InfoOutlined: InfoIcon,
} = MuiIcons;

const CAMPAIGN_EDITING_ENABLED = false;

const CAMPAIGN_HELPER_TEXT = "Campaign identifier used for audience gathering.";

const JOB_LABELS: Record<GetAdvancedTargetingLogsJobName, string> = {
  ip_gather: "I.P. Gather",
  device_gather_visit: "Device Gather (visit)",
};

type JobFilter = GetAdvancedTargetingLogsJobName | "";

interface NumberFieldSpec {
  key: keyof UpdateAdvancedTargetingSettingsDto;
  label: string;
  tooltip: string;
  helper: string;
}

const IP_FIELDS: NumberFieldSpec[] = [
  {
    key: "ipGatherLookbackWindowDays",
    label: "I.P. Gather Lookback Window",
    tooltip:
      "Number of days to look back when gathering visit IPs and suppressing IPs that installed.",
    helper: "Days to look back.",
  },
  {
    key: "ipGatherSuppressionLookbackWindowDays",
    label: "Install/Purchase Lookback Window",
    tooltip:
      "Number of previous complete days to check for install or purchase events when suppressing IPs from the audience.",
    helper: "Previous complete days to check.",
  },
  {
    key: "ipGatherResendAfterDays",
    label: "I.P. Re-send After",
    tooltip:
      "An IP already on the audience is only re-sent once its last send is older than this. Keep it below the audience expiry so entries never lapse.",
    helper: "Days before an IP is re-sent.",
  },
  {
    key: "ipGatherRetentionDays",
    label: "I.P. Retention",
    tooltip:
      "How long a sent IP is remembered after its last send. Match the ad server's expiry so our table mirrors what is actually live there.",
    helper: "Days a sent IP is remembered.",
  },
];

const DEVICE_FIELDS: NumberFieldSpec[] = [
  {
    key: "deviceGatherLookbackWindowDays",
    label: "Device Gather Lookback Window",
    tooltip:
      "Number of days to look back for visits and for the impressions that reveal the device IDs behind those IPs.",
    helper: "Days to look back.",
  },
  {
    key: "deviceGatherResendAfterDays",
    label: "Device Re-send After",
    tooltip:
      "A device already on the audience is only re-sent once its last send is older than this. Keep it below the audience expiry.",
    helper: "Days before a device is re-sent.",
  },
  {
    key: "deviceGatherRetentionDays",
    label: "Device Retention",
    tooltip:
      "How long a sent device is remembered after its last send. Match the ad server's expiry.",
    helper: "Days a sent device is remembered.",
  },
];

const ALL_FIELDS = [...IP_FIELDS, ...DEVICE_FIELDS];

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : value.toLocaleString();

const statusChipColor = (
  status: string
): "success" | "error" | "warning" | "info" | "default" => {
  switch (status?.toLowerCase()) {
    case "success":
      return "success";
    case "partial":
      return "warning";
    case "failed":
      return "error";
    case "running":
      return "info";
    default:
      return "default";
  }
};

interface FieldLabelProps {
  label: string;
  tooltip: string;
}

const FieldLabel = ({ label, tooltip }: FieldLabelProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Tooltip title={tooltip} arrow placement="right">
      <InfoIcon sx={{ fontSize: 16, opacity: 0.5, cursor: "help" }} />
    </Tooltip>
  </Box>
);

const AdvancedTargetingModule = () => {
  const dispatch = useAppDispatch();
  const [tabIndex, setTabIndex] = useState(0);

  const [campaign, setCampaign] = useState("");
  const [numbers, setNumbers] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [jobFilter, setJobFilter] = useState<JobFilter>("");

  const {
    data: settings,
    isLoading: settingsLoading,
    isError: settingsError,
    refetch: refetchSettings,
  } = useGetAdvancedTargetingSettings({
    query: {
      onError: (err) => {
        console.error("Failed to fetch Advanced Targeting settings:", err);
        dispatch(setAlert({
          msg: "Could not fetch Advanced Targeting settings",
          type: AlertTypes.FAILURE,
          d: dispatch,
        }));
      },
    },
  });

  const savedCampaign = settings?.ipGatherCampaign ?? "";
  const savedNumbers = useMemo(() => {
    const values: Record<string, string> = {};
    for (const field of ALL_FIELDS) {
      const value = settings?.[field.key as keyof typeof settings];
      values[field.key] = value != null ? String(value) : "";
    }
    return values;
  }, [settings]);

  useEffect(() => {
    if (settings) {
      setCampaign(settings.ipGatherCampaign ?? "");
      setNumbers(savedNumbers);
    }
  }, [settings, savedNumbers]);

  // A blank field means "leave this one alone", so only non-empty values are validated and sent.
  const invalidFields = ALL_FIELDS.filter((field) => {
    const raw = numbers[field.key] ?? "";
    if (raw === "") return false;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) || parsed < 1;
  });
  const allValid = invalidFields.length === 0;

  const hasChanges =
    !!settings &&
    (ALL_FIELDS.some((field) => (numbers[field.key] ?? "") !== savedNumbers[field.key]) ||
      (CAMPAIGN_EDITING_ENABLED && campaign !== savedCampaign));

  const handleSave = async () => {
    if (!allValid) {
      dispatch(setAlert({
        msg: "Every filled-in window must be a number of days (1 or more)",
        type: AlertTypes.FAILURE,
        d: dispatch,
      }));
      return;
    }
    setSaving(true);
    try {
      const payload: UpdateAdvancedTargetingSettingsDto = {};
      for (const field of ALL_FIELDS) {
        const raw = numbers[field.key] ?? "";
        if (raw !== "") {
          (payload as Record<string, unknown>)[field.key] = parseInt(raw, 10);
        }
      }

      if (CAMPAIGN_EDITING_ENABLED) {
        payload.ipGatherCampaign = campaign.trim();
      }
      await updateAdvancedTargetingSettings(payload);
      await refetchSettings();
      dispatch(setAlert({
        msg: "Advanced Targeting settings saved successfully",
        type: AlertTypes.SUCCESS,
        d: dispatch,
      }));
    } catch (err: any) {
      console.error("Failed to save Advanced Targeting settings:", err);
      const message =
        err.response?.data?.message || "Failed to save Advanced Targeting settings";
      dispatch(setAlert({ msg: message, type: AlertTypes.FAILURE, d: dispatch }));
    } finally {
      setSaving(false);
    }
  };

  const {
    data: logsResponse,
    isLoading: logsLoading,
    isError: logsError,
    refetch: refetchLogs,
  } = useGetAdvancedTargetingLogs(jobFilter ? { jobName: jobFilter } : undefined, {
    query: {
      onError: (err) => {
        console.error("Failed to fetch Advanced Targeting logs:", err);
        dispatch(setAlert({
          msg: "Could not fetch Advanced Targeting run logs",
          type: AlertTypes.FAILURE,
          d: dispatch,
        }));
      },
    },
  });

  const logRows = useMemo(() => {
    const runs = logsResponse?.data || [];
    return [...runs].sort(
      (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );
  }, [logsResponse]);

  const logColumns: GridColDef<AdvancedTargetingRunLogDto>[] = [
    {
      field: "jobName",
      headerName: "Job",
      width: 170,
      renderCell: (params) =>
        params.row.jobName ? JOB_LABELS[params.row.jobName] ?? params.row.jobName : "—",
    },
    {
      field: "startedAt",
      headerName: "Started At",
      width: 170,
      renderCell: (params) => formatDateTime(params.row.startedAt),
    },
    {
      field: "finishedAt",
      headerName: "Finished At",
      width: 170,
      renderCell: (params) => formatDateTime(params.row.finishedAt),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          color={statusChipColor(params.row.status)}
          size="small"
        />
      ),
    },
    { field: "idCampaign", headerName: "Campaign", width: 110 },
    {
      field: "lookbackWindowDays",
      headerName: "Lookback (days)",
      width: 130,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "candidateCount",
      headerName: "Candidate Count",
      width: 130,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => formatCount(params.row.candidateCount),
    },
    {
      field: "skippedCount",
      headerName: "Already Live",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => formatCount(params.row.skippedCount),
    },
    {
      field: "sentCount",
      headerName: "Sent Count",
      width: 110,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => formatCount(params.row.sentCount),
    },
    {
      field: "successCount",
      headerName: "Succeeded",
      width: 110,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => formatCount(params.row.successCount),
    },
    {
      field: "failureCount",
      headerName: "Failed",
      width: 100,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => formatCount(params.row.failureCount),
    },
    {
      field: "errorMessage",
      headerName: "Error",
      width: 220,
      flex: 1,
      renderCell: (params) =>
        params.row.errorMessage ? (
          <Tooltip title={params.row.errorMessage}>
            <Typography variant="body2" color="error" noWrap>
              {params.row.errorMessage}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ opacity: 0.4 }}>
            —
          </Typography>
        ),
    },
  ];

  return (
    <>
      <ReportHeader
        hideTimeNavbar={true}
        breadcrumbs={[
          {
            link: urlService.advancedTargeting(),
            text: "Advanced Targeting",
            icon: <TargetingIcon />,
          },
        ]}
      />
      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ mx: 2, mb: 1, textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Advanced Targeting
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage audience gathering settings and review recent job runs.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ margin: "10px", padding: "20px", borderRadius: "12px" }} elevation={1}>
            <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 3 }}>
              <Tab label="Settings" />
              <Tab label="Logs" />
            </Tabs>

            {/************** SETTINGS TAB *************/}
            {tabIndex === 0 && (
              <>
                {settingsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : settingsError ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="error" sx={{ mb: 2 }}>
                      Could not load Advanced Targeting settings.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => refetchSettings()}
                      sx={{ textTransform: "none" }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ maxWidth: 480 }}>
                    <Box sx={{ mb: 3 }}>
                      <FieldLabel
                        label="I.P. Gather Campaign"
                        tooltip={CAMPAIGN_HELPER_TEXT}
                      />
                      {/* Campaign editing is artificially locked for now
                          (see CAMPAIGN_EDITING_ENABLED above). */}
                      <TextField
                        value={campaign}
                        onChange={(e) => setCampaign(e.target.value)}
                        disabled={!CAMPAIGN_EDITING_ENABLED}
                        fullWidth
                        size="small"
                        variant="outlined"
                        helperText={
                          !CAMPAIGN_EDITING_ENABLED
                            ? "Campaign editing is locked in this version."
                            : CAMPAIGN_HELPER_TEXT
                        }
                      />
                    </Box>

                    {([
                      ["I.P. Audiences", IP_FIELDS],
                      ["Device Audience", DEVICE_FIELDS],
                    ] as [string, NumberFieldSpec[]][]).map(([sectionLabel, fields]) => (
                      <Box key={sectionLabel} sx={{ mb: 1 }}>
                        <Typography
                          variant="overline"
                          color="textSecondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {sectionLabel}
                        </Typography>
                        {fields.map((field) => {
                          const raw = numbers[field.key] ?? "";
                          const invalid = invalidFields.some((f) => f.key === field.key);
                          return (
                            <Box key={field.key} sx={{ mb: 3, mt: 1 }}>
                              <FieldLabel label={field.label} tooltip={field.tooltip} />
                              <TextField
                                value={raw}
                                onChange={(e) =>
                                  setNumbers((prev) => ({
                                    ...prev,
                                    [field.key]: e.target.value,
                                  }))
                                }
                                type="number"
                                inputProps={{ min: 1, step: 1 }}
                                fullWidth
                                size="small"
                                variant="outlined"
                                error={invalid}
                                helperText={
                                  invalid
                                    ? "Must be a number of days (1 or more)."
                                    : field.helper
                                }
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    ))}

                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || !hasChanges || !allValid}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                      >
                        Save Changes
                      </Button>
                      {hasChanges && (
                        <Typography variant="body2" color="warning.main">
                          (unsaved changes)
                        </Typography>
                      )}
                      {!hasChanges && settings?.updatedAt && (
                        <Typography variant="caption" color="textSecondary">
                          Last updated: {formatDateTime(settings.updatedAt)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/************** LOGS TAB *************/}
            {tabIndex === 1 && (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Recent Advanced Targeting job runs (read-only).
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {([["", "All jobs"], ...Object.entries(JOB_LABELS)] as [
                      JobFilter,
                      string
                    ][]).map(([value, label]) => (
                      <Chip
                        key={value || "all"}
                        label={label}
                        size="small"
                        color={jobFilter === value ? "primary" : "default"}
                        variant={jobFilter === value ? "filled" : "outlined"}
                        onClick={() => setJobFilter(value)}
                      />
                    ))}
                  </Box>
                </Box>
                <DynamicDatagrid
                  rows={logRows}
                  columns={logColumns}
                  loading={logsLoading}
                  refreshFunc={refetchLogs}
                  loadError={logsError}
                  pageSize={25}
                  rowHeight={45}
                  style={{ minHeight: "60vh", width: "100%" }}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
      <TieringHistory />
    </>
  );
};

export default AdvancedTargetingModule;
